import React, { useEffect, useState, useCallback } from "react";
import Layout from "../layout/Layout";
import LoadingOverlay from "react-loading-overlay-ts";
import Api from "../Config/ApiConfig";
import Pagination from "../Component/Pagination";
import { toast } from "react-toastify";
import moment from "moment";

const STATUS_COLORS = {
  PENDING:  { bg: "bg-yellow-100", text: "text-yellow-800" },
  ACTIVE:   { bg: "bg-green-100",  text: "text-green-800"  },
  MATCHED:  { bg: "bg-blue-100",   text: "text-blue-800"   },
  EXPIRED:  { bg: "bg-red-100",    text: "text-red-800"    },
};

const PASSENGER_COLORS = {
  CARER:       { bg: "bg-purple-100", text: "text-purple-800" },
  CARE_SEEKER: { bg: "bg-indigo-100", text: "text-indigo-800" },
};

const Badge = ({ value, map }) => {
  const c = map[value] || { bg: "bg-gray-100", text: "text-gray-700" };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      {value?.replace("_", " ")}
    </span>
  );
};

const OtpDot = ({ verified }) =>
  verified ? (
    <span className="inline-flex items-center text-green-600 text-xs font-medium">
      <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      Verified
    </span>
  ) : (
    <span className="inline-flex items-center text-gray-400 text-xs font-medium">
      <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
      Pending
    </span>
  );

export default function MatchboardRequests() {
  const [loading, setLoading]               = useState(true);
  const [data, setData]                     = useState([]);
  const [total, setTotal]                   = useState(0);
  const [page, setPage]                     = useState(1);
  const [limit]                             = useState(10);
  const [search, setSearch]                 = useState("");
  const [statusFilter, setStatusFilter]     = useState("");
  const [passengerFilter, setPassengerFilter] = useState("");
  const [startDate, setStartDate]           = useState("");
  const [endDate, setEndDate]               = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selected, setSelected]             = useState(null);

  const fetchData = useCallback(async (overrides = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      const p           = overrides.page          ?? page;
      const s           = overrides.search        ?? search;
      const st          = overrides.statusFilter  ?? statusFilter;
      const pt          = overrides.passengerFilter ?? passengerFilter;
      const sd          = overrides.startDate     ?? startDate;
      const ed          = overrides.endDate       ?? endDate;

      params.set("page", p);
      params.set("limit", limit);
      if (s)  params.set("search", s);
      if (st) params.set("status", st);
      if (pt) params.set("passengerType", pt);
      if (sd) params.set("startDate", sd);
      if (ed) params.set("endDate", ed);

      const res = await Api.get(`/matchboard-requests?${params.toString()}`);
      if (res.data?.success) {
        setData(res.data.data || []);
        setTotal(res.data.totalItems || 0);
      }
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Unauthorized — please log in again." : "Failed to load matchboard requests.");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, passengerFilter, startDate, endDate, limit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePageClick = (e) => {
    const newPage = e.selected + 1;
    setPage(newPage);
    fetchData({ page: newPage });
  };

  const handleSearch = () => { setPage(1); fetchData({ page: 1 }); };

  const handleClear = () => {
    setSearch(""); setStatusFilter(""); setPassengerFilter("");
    setStartDate(""); setEndDate(""); setPage(1);
    fetchData({ page: 1, search: "", statusFilter: "", passengerFilter: "", startDate: "", endDate: "" });
  };

  const pageCount = Math.ceil(total / limit) || 1;
  const hasFilters = search || statusFilter || passengerFilter || startDate || endDate;

  return (
    <Layout>
      <LoadingOverlay active={loading} text="Loading...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">Matchboard Requests</h2>
                <p className="text-sm text-gray-500 mt-1">Travel companion requests submitted via the matchboard form</p>
              </div>
              <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg font-medium">
                {total} total
              </div>
            </div>

            {/* Filter Panel */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setFiltersExpanded(!filtersExpanded)}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-filter text-emerald-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 m-0">Filters</h3>
                    {!filtersExpanded && hasFilters && (
                      <p className="text-xs text-emerald-600 mt-0.5">Filters active</p>
                    )}
                    {!filtersExpanded && !hasFilters && (
                      <p className="text-xs text-gray-400 mt-0.5">Click to expand</p>
                    )}
                  </div>
                </div>
                <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${filtersExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {filtersExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                    {/* Search */}
                    <div className="lg:col-span-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Search</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Request ID, email, phone, airport..."
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && handleSearch()}
                          className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                        <button onClick={handleSearch} className="common-btn px-4 py-2 text-sm rounded-lg">Search</button>
                        {hasFilters && (
                          <button onClick={handleClear} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">Clear</button>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Status</label>
                      <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); fetchData({ page: 1, statusFilter: e.target.value }); }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white">
                        <option value="">All statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="ACTIVE">Active</option>
                        <option value="MATCHED">Matched</option>
                        <option value="EXPIRED">Expired</option>
                      </select>
                    </div>

                    {/* Passenger Type */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Passenger Type</label>
                      <select value={passengerFilter} onChange={e => { setPassengerFilter(e.target.value); setPage(1); fetchData({ page: 1, passengerFilter: e.target.value }); }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white">
                        <option value="">All types</option>
                        <option value="CARE_SEEKER">Care Seeker</option>
                        <option value="CARER">Carer</option>
                      </select>
                    </div>

                    {/* Travel Date Range */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Travel Date From</label>
                      <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); fetchData({ page: 1, startDate: e.target.value }); }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Travel Date To</label>
                      <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); fetchData({ page: 1, endDate: e.target.value }); }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="user_list_wrap">
                <table className="table table-striped responsive-table" style={{ fontSize: 13, color: "#111827" }}>
                  <thead>
                    <tr>
                      <th>Request ID</th>
                      <th>Type</th>
                      <th>Route</th>
                      <th>Flight</th>
                      <th>Travel Date</th>
                      <th>Contact</th>
                      <th>OTP</th>
                      <th>Package</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row._id}>
                        <td data-label="Request ID" className="font-mono text-xs font-semibold text-gray-700">{row.requestId}</td>
                        <td data-label="Type"><Badge value={row.passengerType} map={PASSENGER_COLORS} /></td>
                        <td data-label="Route">
                          <span className="font-semibold">{row.departureAirport}</span>
                          <span className="text-gray-400 mx-1">→</span>
                          <span className="font-semibold">{row.arrivalAirport}</span>
                          {(row.departureCityName || row.arrivalCityName) && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {row.departureCityName} → {row.arrivalCityName}
                            </div>
                          )}
                        </td>
                        <td data-label="Flight" className="font-mono text-xs text-gray-700">{row.flightNumber || <span className="text-gray-300">—</span>}</td>
                        <td data-label="Travel Date" className="whitespace-nowrap">{moment(row.travelDate).format("DD MMM YYYY")}</td>
                        <td data-label="Contact">
                          <div className="font-medium">{row.contact?.firstName} {row.contact?.lastName}</div>
                          {row.contact?.email && <div className="text-xs text-gray-500">{row.contact.email}</div>}
                          {row.contact?.phone && <div className="text-xs text-gray-500">{row.contact.phone}</div>}
                        </td>
                        <td data-label="OTP">
                          {row.contact?.email && <div><span className="text-gray-400 text-xs">Email: </span><OtpDot verified={row.emailOtpVerified} /></div>}
                          {row.contact?.phone && <div><span className="text-gray-400 text-xs">Phone: </span><OtpDot verified={row.phoneOtpVerified} /></div>}
                        </td>
                        <td data-label="Package">{row.servicePackage ? <span className="text-xs font-medium text-gray-700">{row.servicePackage}</span> : <span className="text-gray-300">—</span>}</td>
                        <td data-label="Status"><Badge value={row.status} map={STATUS_COLORS} /></td>
                        <td data-label="Created" className="whitespace-nowrap text-xs text-gray-500">
                          <div>{moment(row.createdAt).format("DD MMM YYYY")}</div>
                          <div className="text-gray-400">{moment(row.createdAt).format("HH:mm")}</div>
                        </td>
                        <td data-label="Details">
                          <button
                            onClick={() => setSelected(row)}
                            className="text-emerald-600 hover:text-emerald-800 text-xs font-semibold underline"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!loading && data.length === 0 && (
                      <tr>
                        <td colSpan={11} className="text-center py-10 text-gray-400">
                          <i className="fas fa-inbox text-3xl mb-2 block"></i>
                          No matchboard requests found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <Pagination handlePageClick={handlePageClick} pageCount={pageCount} />
          </div>
        </div>
      </LoadingOverlay>

      {/* Detail Modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-screen overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900 m-0">{selected.requestId}</h3>
                <p className="text-xs text-gray-400 mt-0.5">Created {moment(selected.createdAt).format("DD MMM YYYY, HH:mm")}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Detail label="Passenger Type"><Badge value={selected.passengerType} map={PASSENGER_COLORS} /></Detail>
                <Detail label="Status"><Badge value={selected.status} map={STATUS_COLORS} /></Detail>
                <Detail label="Departure">{selected.departureAirport}</Detail>
                <Detail label="Arrival">{selected.arrivalAirport}</Detail>
                <Detail label="Flight Number">{selected.flightNumber || "—"}</Detail>
                <Detail label="Travel Date">{moment(selected.travelDate).format("DD MMM YYYY")}</Detail>
                {selected.servicePackage && <Detail label="Package">{selected.servicePackage}</Detail>}
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Contact</p>
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Name">{selected.contact?.firstName} {selected.contact?.lastName}</Detail>
                  {selected.contact?.email && <Detail label="Email">{selected.contact.email}</Detail>}
                  {selected.contact?.phone && <Detail label="Phone">{selected.contact.phone}</Detail>}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">OTP Verification</p>
                <div className="grid grid-cols-2 gap-3">
                  {selected.contact?.email && <Detail label="Email OTP"><OtpDot verified={selected.emailOtpVerified} /></Detail>}
                  {selected.contact?.phone && <Detail label="Phone OTP"><OtpDot verified={selected.phoneOtpVerified} /></Detail>}
                </div>
              </div>

              {selected.preferences && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Preferences</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Detail label="Gender">{selected.preferences.gender || "—"}</Detail>
                    <Detail label="Languages">{selected.preferences.languages?.join(", ") || "—"}</Detail>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <p className="text-xs text-gray-400 font-medium mb-0.5">{label}</p>
      <div className="text-sm font-semibold text-gray-800">{children}</div>
    </div>
  );
}
